import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Select, Modal } from "antd";
import { BACKEND_BASE_URL } from "../../config/app.config";
import { StateContext } from "../../store/context";
import "./AnnualReportManualEntry.css";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type StockOption = {
  value: string;
  label: string;
};

type StatusState = {
  type: "success" | "error" | "info" | "warning";
  message: string;
} | null;

type SavedManualRow = {
  id: number;
  stock_id: number;
  ticker_symbol: string;
  company_name?: string | null;
  fiscal_year: number;
  pdf_name?: string | null;
  qualitative_preview?: string | null;
  qualitative_length?: number;
  has_shareholders?: number | boolean;
  created_at?: string;
  updated_at?: string;
};

type AnalysisSummary = {
  pdfName: string;
  qualitativeLength: number;
  wordCount: number;
  rawLength: number;
  llmModel: string;
  llmProvider: string;
  pdfExtractor: string;
  analyzedAt: string;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const resolveApiBaseUrl = () => {
  const envUrl =
    process.env.REACT_APP_ANNUAL_REPORT_API ??
    process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_BASE_URL ??
    "";

  if (typeof window !== "undefined") {
    const origin = window.location.origin || "";
    const isHttps = window.location.protocol === "https:";
    const candidate = (envUrl || BACKEND_BASE_URL || "").replace(/\/+$/, "");

    if (isHttps && candidate.startsWith("http://")) {
      if (origin && !origin.includes("localhost")) {
        return origin;
      }
      return candidate.replace(/^http:\/\//, "https://");
    }
    if (candidate) return candidate;
    if (origin) return origin;
  }

  return (envUrl || BACKEND_BASE_URL || "http://localhost:8000").replace(/\/+$/, "");
};

const API_BASE_URL = resolveApiBaseUrl();
const toAuthorizationHeader = (token: any) => {
  const trimmed = String(token || "").trim();
  if (!trimmed) {
    return "";
  }
  return /^bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`;
};

const PROGRESS_MESSAGES = [
  "Reading PDF document...",
  "Uploading to LLMWhisperer...",
  "Extracting raw text from all pages...",
  "Running LLM extraction for 14 qualitative sections...",
  "Removing financial statement residue...",
  "Cleaning and normalizing output...",
  "Almost there — finalizing extraction...",
];

const formatTimestamp = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const countWords = (text: string) =>
  text.split(/\s+/).filter(Boolean).length;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AnnualReportManualEntry: React.FC = (): React.ReactElement => {
  const { authUser } = useContext(StateContext);

  /* ---- state ---- */
  const [stockOptions, setStockOptions] = useState<StockOption[]>([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [selectedTicker, setSelectedTicker] = useState("");
  const [fiscalYear, setFiscalYear] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [qualitativeText, setQualitativeText] = useState("");
  const [status, setStatus] = useState<StatusState>(null);
  const [savedRows, setSavedRows] = useState<SavedManualRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState("");
  const [rowsContext, setRowsContext] = useState("Showing latest 20 rows.");
  const [analysisSummary, setAnalysisSummary] = useState<AnalysisSummary | null>(null);
  const [progressMessage, setProgressMessage] = useState("");
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [deletingRowId, setDeletingRowId] = useState<number | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const textareaRef = useRef<HTMLDivElement>(null);

  /* ---- derived ---- */
  const wordCount = useMemo(() => countWords(qualitativeText), [qualitativeText]);
  const charCount = qualitativeText.length;

  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem("token");
    const authorization = toAuthorizationHeader(token);
    if (authorization) headers.Authorization = authorization;
    if (authUser?.role_id) headers.role_id = String(authUser.role_id);
    if (authUser?.email) headers["x-user-email"] = String(authUser.email);
    if (authUser?.username) headers["x-username"] = String(authUser.username);
    return headers;
  }, [authUser?.role_id, authUser?.email, authUser?.username]);

  const canSave =
    Boolean(selectedTicker) &&
    /^\d{4}$/.test(fiscalYear) &&
    Boolean(qualitativeText.trim()) &&
    Boolean(analysisSummary) &&
    !extracting &&
    !submitting;

  const canAnalyze = Boolean(pdfFile) && !extracting && !submitting;

  const saveBlockerMessage = !pdfFile
    ? "Step 1: Upload a PDF first."
    : !analysisSummary
      ? "Step 2: Click Analyze to extract qualitative data."
      : !selectedTicker
        ? "Select a stock company before saving."
        : !/^\d{4}$/.test(fiscalYear)
          ? "Enter a valid fiscal year (YYYY) before saving."
          : !qualitativeText.trim()
            ? "Review extracted text before saving."
            : null;

  /* ---- progress animation ---- */
  const startProgress = useCallback(() => {
    let idx = 0;
    setProgressMessage(PROGRESS_MESSAGES[0]);
    progressIntervalRef.current = setInterval(() => {
      idx = Math.min(idx + 1, PROGRESS_MESSAGES.length - 1);
      setProgressMessage(PROGRESS_MESSAGES[idx]);
    }, 4500);
  }, []);

  const stopProgress = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setProgressMessage("");
  }, []);

  useEffect(() => () => stopProgress(), [stopProgress]);

  /* ---- fetch stocks ---- */
  useEffect(() => {
    const fetchStocks = async () => {
      setStocksLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/all-stocks`, {
          headers: authHeaders,
        });
        const sections = response?.data?.data || response?.data || [];
        const flattened = Array.isArray(sections)
          ? sections.flatMap((section: any) => section?.options || [])
          : [];
        const deduped = new Map<string, any>();
        flattened.forEach((stock: any) => {
          const ticker = String(stock?.ticker_symbol || "").trim().toUpperCase();
          if (!ticker || deduped.has(ticker)) return;
          deduped.set(ticker, stock);
        });
        const options = Array.from(deduped.values())
          .filter(
            (stock) =>
              stock?.status === 1 ||
              stock?.status === "1" ||
              typeof stock?.status === "undefined"
          )
          .map((stock) => ({
            value: stock.ticker_symbol,
            label: `${stock.company_name || stock.ticker_symbol} (${stock.ticker_symbol})`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setStockOptions(options);
      } catch {
        setStatus({
          type: "error",
          message: "Failed to load stock list. Please refresh and try again.",
        });
      } finally {
        setStocksLoading(false);
      }
    };
    fetchStocks();
  }, [authHeaders]);

  /* ---- fetch saved rows ---- */
  const fetchSavedRows = useCallback(
    async (options?: { tickerSymbol?: string; fiscalYear?: number | null }) => {
      setRowsLoading(true);
      setRowsError("");

      const params: Record<string, string | number> = { limit: 20 };
      const ticker = String(options?.tickerSymbol || "").trim().toUpperCase();
      const yearValue = options?.fiscalYear;
      const hasYear =
        typeof yearValue === "number" &&
        Number.isInteger(yearValue) &&
        yearValue >= 1900 &&
        yearValue <= 2100;
      if (ticker) params.ticker_symbol = ticker;
      if (hasYear) params.fiscal_year = Number(yearValue);

      try {
        const response = await axios.get(`${API_BASE_URL}/api/annual-report/manual`, {
          headers: authHeaders,
          params,
        });
        const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        setSavedRows(rows);

        if (ticker && hasYear) {
          setRowsContext(`Showing rows for ${ticker} FY ${yearValue}.`);
        } else if (ticker) {
          setRowsContext(`Showing rows for ${ticker}.`);
        } else if (hasYear) {
          setRowsContext(`Showing rows for fiscal year ${yearValue}.`);
        } else {
          setRowsContext("Showing latest 20 rows.");
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.details ||
          "Failed to load saved rows.";
        setRowsError(message);
        setSavedRows([]);
      } finally {
        setRowsLoading(false);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    fetchSavedRows();
  }, [fetchSavedRows]);

  /* ---- handlers ---- */
  const handleExtractFromPdf = async () => {
    if (!pdfFile) {
      setStatus({ type: "error", message: "Please select a PDF file first." });
      return;
    }

    setExtracting(true);
    setAnalysisSummary(null);
    startProgress();
    setStatus({
      type: "info",
      message: "Extracting qualitative text — this may take 1-3 minutes...",
    });

    try {
      const readAsDataUrl = (sourceFile: File) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(new Error("Failed to read PDF file."));
          reader.readAsDataURL(sourceFile);
        });
      const pdfBase64 = await readAsDataUrl(pdfFile);

      const response = await axios.post(
        `${API_BASE_URL}/api/annual-report/manual/extract`,
        { pdf_base64: pdfBase64, pdf_name: pdfFile.name },
        { headers: authHeaders }
      );

      const extracted = response?.data?.extracted || {};
      const extractedText = String(extracted?.qualitative_text || "").trim();
      const extractedModels = Array.isArray(extracted?.llm_models)
        ? extracted.llm_models
          .map((model: any) => String(model || "").trim())
          .filter(Boolean)
        : [];
      if (!extractedText) {
        throw new Error("No qualitative text was extracted from the uploaded PDF.");
      }

      const nextPdfName = String(extracted?.pdf_name || pdfFile.name || "").trim();
      if (nextPdfName) setPdfName(nextPdfName);
      setQualitativeText(extractedText);
      setAnalysisSummary({
        pdfName: nextPdfName || pdfFile.name,
        qualitativeLength: Number(extracted?.qualitative_text_length || 0),
        wordCount: Number(extracted?.word_count || countWords(extractedText)),
        rawLength: Number(extracted?.raw_text_length || 0),
        llmModel: extractedModels.length
          ? extractedModels.join(" -> ")
          : String(
            extracted?.llm_model ||
              "minimax/minimax-m2.5 -> moonshotai/kimi-k2.5"
          ),
        llmProvider: String(extracted?.llm_provider || "openrouter"),
        pdfExtractor: String(extracted?.pdf_extractor || "llmwhisperer"),
        analyzedAt: new Date().toISOString(),
      });
      setStatus({
        type: "success",
        message: 'Analysis complete! Review the qualitative text, then click "Save To Database".',
      });

      setTimeout(() => {
        textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        error?.message ||
        "Failed to extract qualitative text from PDF.";
      setAnalysisSummary(null);
      setStatus({ type: "error", message: errorMessage });
    } finally {
      setExtracting(false);
      stopProgress();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) return;
    setShowSaveConfirm(true);
  };

  const confirmSave = async () => {
    setShowSaveConfirm(false);
    setSubmitting(true);
    setStatus({ type: "info", message: "Saving annual report text..." });

    const year = fiscalYear.trim();

    try {
      const payload = {
        ticker_symbol: selectedTicker,
        fiscal_year: Number(year),
        pdf_name: pdfName.trim() || null,
        qualitative_text: qualitativeText.trim(),
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/annual-report/manual`,
        payload,
        { headers: authHeaders }
      );

      const saved = response?.data?.savedReport;
      setStatus({
        type: "success",
        message: saved
          ? `Saved successfully: ${saved.ticker_symbol} FY ${saved.fiscal_year} (${saved.word_count?.toLocaleString() || 0} words).`
          : "Saved successfully.",
      });
      await fetchSavedRows({
        tickerSymbol: selectedTicker,
        fiscalYear: Number(year),
      });
      setPdfFile(null);
      setQualitativeText("");
      setPdfName("");
      setAnalysisSummary(null);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        "Failed to save annual report text.";
      setStatus({ type: "error", message: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRow = async (rowId: number) => {
    setDeletingRowId(rowId);
    try {
      await axios.delete(`${API_BASE_URL}/api/annual-report/manual/${rowId}`, {
        headers: authHeaders,
      });
      setSavedRows((prev) => prev.filter((r) => r.id !== rowId));
      if (expandedRowId === rowId) setExpandedRowId(null);
      setStatus({ type: "success", message: "Row deleted successfully." });
    } catch (error: any) {
      const msg =
        error?.response?.data?.error || "Failed to delete row.";
      setStatus({ type: "error", message: msg });
    } finally {
      setDeletingRowId(null);
    }
  };

  const handleReset = () => {
    setPdfFile(null);
    setAnalysisSummary(null);
    setQualitativeText("");
    setPdfName("");
    setStatus(null);
    setProgressMessage("");
  };

  /* ---- step state helper ---- */
  const stepState = (step: 1 | 2 | 3) => {
    if (step === 1) return pdfFile ? "done" : "pending";
    if (step === 2) {
      if (analysisSummary) return "done";
      if (extracting) return "active";
      return "pending";
    }
    if (canSave) return "ready";
    return "pending";
  };

  const stepIcon = (state: string) => {
    if (state === "done") {
      return (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    if (state === "active") {
      return <span className="arm-spinner" />;
    }
    return <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.5 }}>•</span>;
  };

  /* ---- render ---- */
  return (
    <div className="arm-page">
      <div className="arm-container">

        {/* ── Header ── */}
        <div className="arm-header">
          <div className="arm-header-pattern" />
          <div className="arm-header-content">
            <h1>Annual Report Qualitative Ingestion</h1>
            <p>
              Upload an annual report PDF, extract qualitative narrative across 14 report sections using AI, review, and save to the database.
              Financial statements are excluded — Falcon AI handles those separately.
            </p>
            <div className="arm-header-tags">
              {[
                { label: "LLMWhisperer", icon: "📄" },
                { label: "minimax + kimi-k2.5", icon: "🤖" },
                { label: "14 Qualitative Sections", icon: "✍️" },
              ].map((tag) => (
                <span key={tag.label} className="arm-tag">
                  <span>{tag.icon}</span> {tag.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Main Form Card ── */}
        <div className="arm-card">
          <form onSubmit={handleSubmit}>

            {/* Step Indicators */}
            <div className="arm-steps">
              {([
                { step: 1 as const, title: "Upload PDF", desc: pdfFile ? pdfFile.name : "Select an annual report PDF." },
                { step: 2 as const, title: "Analyze", desc: analysisSummary ? `${analysisSummary.wordCount.toLocaleString()} words extracted` : extracting ? progressMessage || "Extraction running..." : "Run AI to extract 14 qualitative sections." },
                { step: 3 as const, title: "Save to DB", desc: canSave ? "Ready to save." : (saveBlockerMessage || "Complete required fields.") },
              ]).map(({ step, title, desc }) => {
                const state = stepState(step);
                return (
                  <div key={step} className={`arm-step ${state}`}>
                    <div className="arm-step-icon">{stepIcon(state)}</div>
                    <div>
                      <p className="arm-step-title">Step {step}: {title}</p>
                      <p className="arm-step-desc">{desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Step 1: Upload ── */}
            <div className="arm-section arm-section-upload">
              <div className="arm-section-header">
                <p className="arm-section-label">📄 Upload Annual Report PDF</p>
                <span className={`arm-badge ${pdfFile ? "ready-badge" : "waiting-badge"}`}>
                  {pdfFile ? "✓ Ready" : "Waiting"}
                </span>
              </div>
              <input
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setPdfFile(file);
                  setAnalysisSummary(null);
                  setQualitativeText("");
                  if (file) {
                    setPdfName(file.name);
                    setStatus({
                      type: "info",
                      message: 'PDF uploaded. Click "Analyze PDF" to extract qualitative data.',
                    });
                  } else {
                    setStatus(null);
                  }
                }}
                className="arm-file-input"
              />
              {pdfFile && (
                <p className="arm-file-hint">
                  <span>📎</span> {pdfFile.name}
                  <span style={{ color: "#cbd5e1" }}>•</span>
                  {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
            </div>

            {/* ── Step 2: Analyze ── */}
            <div className="arm-section arm-section-analyze">
              <div className="arm-analyze-row">
                <div>
                  <p className="arm-analyze-label">🤖 Analyze PDF</p>
                  <p className="arm-analyze-sub">
                    LLMWhisperer extracts raw text, then the LLM filters and keeps the 14 qualitative report sections.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleExtractFromPdf}
                  disabled={!canAnalyze}
                  className="arm-btn-primary"
                >
                  {extracting ? (
                    <>
                      <span className="arm-spinner" />
                      Analyzing...
                    </>
                  ) : (
                    "Analyze PDF"
                  )}
                </button>
              </div>

              {/* Progress bar */}
              {extracting && (
                <div className="arm-progress-wrap">
                  <div className="arm-progress-track">
                    <div className="arm-progress-fill" />
                  </div>
                  <p className="arm-progress-msg">{progressMessage}</p>
                </div>
              )}

              {/* Analysis summary */}
              {analysisSummary && (
                <div className="arm-summary-grid">
                  {[
                    { label: "Model", value: analysisSummary.llmModel },
                    { label: "Provider", value: analysisSummary.llmProvider },
                    { label: "Extractor", value: analysisSummary.pdfExtractor },
                    { label: "Words", value: analysisSummary.wordCount.toLocaleString() },
                    { label: "Characters", value: analysisSummary.qualitativeLength.toLocaleString() },
                    { label: "Analyzed", value: formatTimestamp(analysisSummary.analyzedAt) },
                  ].map((item) => (
                    <div key={item.label} className="arm-summary-item">
                      <span className="arm-summary-item-label">{item.label}</span>
                      <span className="arm-summary-item-value">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Metadata Fields ── */}
            <div className="arm-fields-grid">
              <div>
                <label className="arm-field-label">
                  Stock Company <span className="req">*</span>
                </label>
                <Select
                  showSearch
                  placeholder="Search and select..."
                  value={selectedTicker || undefined}
                  onChange={(value) => setSelectedTicker(String(value || ""))}
                  options={stockOptions}
                  loading={stocksLoading}
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  disabled={extracting || submitting}
                />
              </div>
              <div>
                <label className="arm-field-label">
                  Fiscal Year <span className="req">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={fiscalYear}
                  onChange={(event) => setFiscalYear(event.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="e.g. 2024"
                  disabled={extracting || submitting}
                  className="arm-input"
                />
              </div>
              <div>
                <label className="arm-field-label">
                  PDF Name <span className="opt">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={pdfName}
                  onChange={(event) => setPdfName(event.target.value)}
                  placeholder="e.g. ACL_Annual_Report_2024.pdf"
                  disabled={extracting || submitting}
                  className="arm-input"
                />
              </div>
            </div>

            {/* ── Textarea ── */}
            <div ref={textareaRef}>
              <div className="arm-textarea-header">
                <label className="arm-field-label" style={{ margin: 0 }}>
                  Qualitative Text <span className="req">*</span>
                </label>
                {qualitativeText && (
                  <div className="arm-textarea-counts">
                    <span>{wordCount.toLocaleString()} words</span>
                    <span style={{ color: "#e2e8f0" }}>|</span>
                    <span>{charCount.toLocaleString()} chars</span>
                  </div>
                )}
              </div>
              <textarea
                rows={18}
                value={qualitativeText}
                onChange={(event) => setQualitativeText(event.target.value)}
                disabled={extracting || submitting}
                placeholder={
                  analysisSummary
                    ? "Review or edit the extracted qualitative content before saving..."
                    : "Upload and analyze a PDF to auto-fill qualitative content."
                }
                className="arm-textarea"
              />
            </div>

            {/* ── Status Banner ── */}
            {status && (
              <div className={`arm-status ${status.type}`}>
                <span className="arm-status-icon">
                  {status.type === "success" ? "✅" : status.type === "error" ? "❌" : status.type === "warning" ? "⚠️" : "ℹ️"}
                </span>
                <p>{status.message}</p>
              </div>
            )}

            {/* ── Save Bar ── */}
            <div className="arm-save-bar">
              <div className="arm-save-bar-inner">
                <div>
                  <p className="arm-save-bar-label">
                    Step 3: Save qualitative narrative to <code>annual_reports</code>
                  </p>
                  {!canSave && saveBlockerMessage && (
                    <p className="arm-save-bar-warn">⚠ {saveBlockerMessage}</p>
                  )}
                </div>
                <div className="arm-save-bar-actions">
                  <button
                    type="submit"
                    disabled={!canSave}
                    className="arm-btn-primary"
                  >
                    {submitting ? (
                      <>
                        <span className="arm-spinner" />
                        Saving...
                      </>
                    ) : (
                      <>💾 Save To Database</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setQualitativeText(""); setStatus(null); }}
                    disabled={extracting || submitting}
                    className="arm-btn-secondary"
                  >
                    Clear Text
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={extracting || submitting}
                    className="arm-btn-secondary"
                  >
                    Reset Flow
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* ── Saved Rows Table ── */}
        <div className="arm-card arm-table-card">
          <div className="arm-table-header">
            <div>
              <h2>📋 Saved Reports</h2>
              <p>{rowsContext}</p>
            </div>
            <div className="arm-table-actions">
              <button
                type="button"
                onClick={() =>
                  fetchSavedRows({
                    tickerSymbol: selectedTicker || undefined,
                    fiscalYear: /^\d{4}$/.test(fiscalYear) ? Number(fiscalYear) : null,
                  })
                }
                className="arm-btn-filter"
              >
                🔍 Filter Matching
              </button>
              <button
                type="button"
                onClick={() => fetchSavedRows()}
                className="arm-btn-ghost"
              >
                Show Recent
              </button>
            </div>
          </div>

          {rowsError && (
            <div className="arm-table-error">{rowsError}</div>
          )}

          <div className="arm-table-wrapper">
            {rowsLoading ? (
              <div className="arm-loading-state">
                <span className="arm-spinner-blue" />
                <span className="arm-loading-text">Loading rows...</span>
              </div>
            ) : savedRows.length ? (
              <table className="arm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Ticker</th>
                    <th>Company</th>
                    <th>FY</th>
                    <th>PDF Name</th>
                    <th className="text-right">Text Length</th>
                    <th className="text-center">Shareholders</th>
                    <th>Created</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedRows.map((row) => {
                    const isExpanded = expandedRowId === row.id;
                    const isDeleting = deletingRowId === row.id;
                    return (
                      <React.Fragment key={row.id}>
                        <tr className={isExpanded ? "expanded" : ""}>
                          <td className="mono">{row.id}</td>
                          <td>
                            <span className="arm-ticker-badge">
                              {row.ticker_symbol || "-"}
                            </span>
                          </td>
                          <td>{row.company_name || "-"}</td>
                          <td style={{ fontWeight: 600 }}>{row.fiscal_year || "-"}</td>
                          <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.pdf_name || "-"}
                          </td>
                          <td className="text-right mono">
                            {(row.qualitative_length ?? 0).toLocaleString()}
                          </td>
                          <td className="text-center">
                            {Number(row.has_shareholders || 0) > 0 ? (
                              <span className="arm-shareholders-yes">Yes</span>
                            ) : (
                              <span className="arm-shareholders-no">No</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, color: "#64748b" }}>
                            {formatTimestamp(row.created_at)}
                          </td>
                          <td>
                            <div className="arm-row-actions">
                              <button
                                type="button"
                                onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                                className="arm-btn-preview"
                              >
                                {isExpanded ? "Hide" : "Preview"}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  Modal.confirm({
                                    title: "Delete this entry?",
                                    content: `This will permanently remove the report for ${row.ticker_symbol} FY ${row.fiscal_year}.`,
                                    okText: "Delete",
                                    okType: "danger",
                                    cancelText: "Cancel",
                                    onOk: () => handleDeleteRow(row.id),
                                  });
                                }}
                                disabled={isDeleting}
                                className="arm-btn-delete"
                              >
                                {isDeleting ? "..." : "Delete"}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="arm-expanded-row">
                            <td colSpan={9} className="arm-expanded-cell">
                              <div className="arm-preview-box">
                                <p className="arm-preview-label">Qualitative Text Preview</p>
                                <p className="arm-preview-text">
                                  {row.qualitative_preview || "No preview available."}
                                </p>
                                {row.qualitative_preview && row.qualitative_preview.length >= 290 && (
                                  <p className="arm-preview-truncate">
                                    Showing first ~300 characters. Full text: {(row.qualitative_length ?? 0).toLocaleString()} chars.
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="arm-empty-state">
                <span className="arm-empty-icon">📭</span>
                <p className="arm-empty-text">No rows found for the current filter.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Confirmation Modal ── */}
      <Modal
        title="Confirm Save"
        open={showSaveConfirm}
        onOk={confirmSave}
        onCancel={() => setShowSaveConfirm(false)}
        okText="Save to Database"
        cancelText="Cancel"
        okButtonProps={{ style: { background: "#2563eb", borderColor: "#2563eb" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "8px 0" }}>
          <p style={{ margin: 0, fontSize: 14, color: "#475569" }}>
            This will save the qualitative text for:
          </p>
          <div style={{ borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", padding: 14, fontSize: 14, display: "flex", flexDirection: "column", gap: 6 }}>
            <p style={{ margin: 0 }}><strong>Ticker:</strong> {selectedTicker}</p>
            <p style={{ margin: 0 }}><strong>Fiscal Year:</strong> {fiscalYear}</p>
            <p style={{ margin: 0 }}><strong>Words:</strong> {wordCount.toLocaleString()}</p>
            <p style={{ margin: 0 }}><strong>Characters:</strong> {charCount.toLocaleString()}</p>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: "#d97706" }}>
            If an entry already exists for this ticker + year, it will be <strong>updated</strong>.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AnnualReportManualEntry;
